import type { MonitoringProperties } from './types';

/** What the capture layer supplies: a deduping reporter. */
export type Capture = (error: unknown, properties?: MonitoringProperties) => void;

/**
 * Listens for errors nothing in React caught, and returns a teardown.
 *
 * The `setTimeout` in each handler is load-bearing, not incidental: it lets a
 * boundary that is about to catch the same error claim the dedupe first and
 * keep its component stack. See `./README.md`.
 */
export const installGlobalHandlers = (capture: Capture): (() => void) => {
  const onError = (event: globalThis.ErrorEvent) => {
    // `event.error` is absent for cross-origin script errors, where the message
    // is all the browser will give us.
    const thrown = event.error ?? event.message;
    setTimeout(
      () => capture(thrown, { source: 'window-error', route: window.location.pathname }),
      0,
    );
  };

  const onRejection = (event: PromiseRejectionEvent) => {
    setTimeout(
      () =>
        capture(event.reason, { source: 'unhandledrejection', route: window.location.pathname }),
      0,
    );
  };

  window.addEventListener('error', onError);
  window.addEventListener('unhandledrejection', onRejection);

  return () => {
    window.removeEventListener('error', onError);
    window.removeEventListener('unhandledrejection', onRejection);
  };
};
